import {
  CheckCircle,
  Clock,
  DollarSign,
  Funnel,
  Layers,
  LayoutList,
  Save,
  Search,
  X,
  XCircle,
  AlertTriangle,

} from 'lucide-react';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'sonner';
import { ABORT_ERROR, makeRESTApiRequest } from '../../helpers/api';
import { splitQualiaTaskError, toastOrderUpdateResult } from '../../helpers/qualia';
import './order.scss';
import {
  ENDPOINTS,
  getStartDateAndEndDateByRange,
  INDIVIDUAL_ORDERS,
  ORDER_STATUS_RANGE,
  ORDER_TABLE_COLUMNS_DISABLED,
  ORDER_TYPES,
  SERVICES_RELATED_FILTERS
} from '../../helpers/constants';
import {
  getFullAddress,
  hasFilterValues,
  isClient,
  isStaff,
  updatedServiceFiltereFields
} from '../../helpers/global';
import useAppChoices from '../../hooks/useAppChoices';
import useQuery from '../../hooks/useQueryParams';
import DataTable from '../common/data-table/DataTable';
import ServiceSlidingBar from './ServiceSlidingBar';
import OrderFilterBuilder from './OrderFilterBuilder';
import OrderPreference from './OrderPreference';
import SaveEditFilterModel from './SaveEditFilterModel';
import SavedFiltersList from './SavedFiltersList';
import ChipSkeletonLoader from '../common/ChipSkeletonLoader';
import SaveAsOrUpdateModel from './SaveAsOrUpdateModel';
import { getOrderTableColumns } from './orderTableColumns';
import OrderRowContextMenu from './OrderRowContextMenu';
import { getOrderContextMenuItems } from './orderContextMenuItems';

const TAB_KEYS = {
  ALL: 'ALL',
  IN_PROGRESS: 'IN_PROGRESS',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  DELIVERED_NOT_PAID: 'DELIVERED_NOT_PAID',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

const NEEDS_ATTENTION_STATUSES = ['Hold', 'Link Sent'];

const ORDER_TABS = Object.keys(TAB_KEYS);

const INPROGRESS_STATUSES = [
  'New Order',
  'Due Diligence',
  'Update',
  'Information Requested',
  'Preliminary Report',
  'Information Received',
  'Ready for QA',
  'Revision',
  'Hold',
  'Link Sent',
  'Pending'
];
const CLIENT_IN_PROGRESS_STATUSES = INPROGRESS_STATUSES.filter(
  status => !NEEDS_ATTENTION_STATUSES.includes(status)
);
const COMPLETED_STATUSES = ['Paid'];
const DELIVERED_NOT_PAID_STATUSES = ['Pending To Pay'];
const CANCELLED_STATUSES = ['Cancelled', 'Refunded', 'Refund Requested'];

const UPCOMING_CLOSINGS_STATUSES = [...INPROGRESS_STATUSES, 'Pending To Pay', 'Refund Requested'];

// Order Tabs Config
const ORDER_TABS_CONFIG = {
  [TAB_KEYS.ALL]: {
    label: 'All',
    Icon: Layers,
    statuses: [...INPROGRESS_STATUSES, ...COMPLETED_STATUSES, ...CANCELLED_STATUSES, 'Pending To Pay'],
    bgColor: '#000000',
    textColor: 'white'
  },
  [TAB_KEYS.IN_PROGRESS]: {
    label: 'In Progress',
    Icon: Clock,
    statuses: INPROGRESS_STATUSES,
    bgColor: '#FACC15',
    textColor: 'black'
  },
  [TAB_KEYS.NEEDS_ATTENTION]: {
    label: 'Needs Attention',
    Icon: AlertTriangle,
    statuses: NEEDS_ATTENTION_STATUSES,
    bgColor: '#F97316',
    textColor: 'white',
    clientOnly: true
  },
  [TAB_KEYS.DELIVERED_NOT_PAID]: {
    label: 'Completed - Unpaid',
    Icon: DollarSign,
    statuses: DELIVERED_NOT_PAID_STATUSES,
    bgColor: '#ff8904',
    textColor: 'white'
  },
  [TAB_KEYS.COMPLETED]: {
    label: 'Completed - Paid',
    Icon: CheckCircle,
    statuses: COMPLETED_STATUSES,
    bgColor: '#16A34A',
    textColor: 'white'
  },
  [TAB_KEYS.CANCELLED]: {
    label: 'Cancelled',
    Icon: XCircle,
    statuses: CANCELLED_STATUSES,
    bgColor: '#EF4444',
    textColor: 'white'
  }
};

const getTabBaseFilter = (tab, isClientUser = false) => {
  if (tab === TAB_KEYS.IN_PROGRESS && isClientUser) {
    return CLIENT_IN_PROGRESS_STATUSES;
  }
  return ORDER_TABS_CONFIG[tab].statuses;
};

// build the combined filter for the API request
const buildCombinedFilter = (orderFilterMetadata, tab, isClientUser = false) => {
  const appliedFilters = [];

  // activeTab filter
  appliedFilters.push({
    key: 'activeTab',
    value: getTabBaseFilter(tab, isClientUser)
  });

  // Process each filter from metadata
  orderFilterMetadata
    .filter(filter => hasFilterValues(filter, filter.values))
    .forEach(filter => {
      appliedFilters.push({
        key: filter.key,
        value: filter.values,
        operator: filter.operator || null
      });
    });

  return appliedFilters;
};

/** Apply saved filter columnOrdering [{ key, hidden }, …] (array index = display order). */
const normalizeOrdersTablePrefs = (allColumns, columnOrdering = []) => {
  const validKeys = new Set(allColumns.map(c => c.key));
  const defaultHidden = new Set(allColumns.filter(c => c.hiddenByDefault).map(c => c.key));
  const seen = new Set();
  const columnOrder = [];
  const hiddenColumnKeys = [];

  for (const col of columnOrdering) {
    if (!col?.key || !validKeys.has(col.key) || seen.has(col.key)) continue;
    seen.add(col.key);
    columnOrder.push(col.key);
    if (col.hidden) {
      hiddenColumnKeys.push(col.key);
    }
  }

  for (const c of allColumns) {
    if (seen.has(c.key)) continue;
    seen.add(c.key);
    columnOrder.push(c.key);
    if (defaultHidden.has(c.key)) {
      hiddenColumnKeys.push(c.key);
    }
  }

  return { columnOrder, hiddenColumnKeys };
};

/** Serialize table prefs to saved-filter columnOrdering (array index = column order). */
const buildColumnOrderingFromTablePrefs = (allColumns, tablePrefs) => {
  const validKeys = new Set(allColumns.map(c => c.key));
  const hiddenSet = new Set(tablePrefs.hiddenColumnKeys || []);
  const order = (tablePrefs.columnOrder || []).filter(k => validKeys.has(k));
  const seen = new Set(order);

  for (const c of allColumns) {
    if (!seen.has(c.key)) {
      order.push(c.key);
      seen.add(c.key);
    }
  }

  return order.map(key => ({
    key,
    hidden: hiddenSet.has(key)
  }));
};

const cloneOrdersTablePrefs = prefs => ({
  columnOrder: [...(prefs.columnOrder || [])],
  hiddenColumnKeys: [...(prefs.hiddenColumnKeys || [])]
});

const sortOrdersPinnedFirst = ordersList => {
  const pinned = [];
  const unpinned = [];

  ordersList.forEach(order => {
    if (order.pinned) {
      pinned.push(order);
    } else {
      unpinned.push(order);
    }
  });

  return [...pinned, ...unpinned];
};

function Orders({ createdService, onServiceHandled }) {
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const isStaffUser = isStaff(user?.role);

  const [orders, setOrders] = useState([]);
  const [pinnedOrderCount, setPinnedOrderCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filters, setFilters] = useState({});

  const {
    orderStatus,
    organizationId,
    range,
    orderId,
    serviceId,
    orderType: orderTypeFromQuery,
    reportType,
    service: serviceFromQuery,
    assignedProcessorId,
    sortKey,
    sortDirection
  } = useQuery();

  const users = useAppChoices('users');
  const organizations = useAppChoices('organizations');
  const municipalityOptions = useAppChoices('cities');
  const countyOptions = useAppChoices('counties');
  const lienSearchStatuses = useAppChoices('Lien Search Status');
  const estoppelStatuses = useAppChoices('Estoppel Status');
  const surveyStatuses = useAppChoices('Survey Status');

  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.IN_PROGRESS);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [showOrderPreference, setShowOrderPreference] = useState(false);

  const [serviceMetadata, setServiceMetadata] = useState({
    service: null,
    orderType: null,
    rowOrderId: null
  });

  const [showSaveMeta, setShowSaveMeta] = useState({ show: false, isUpdating: false });
  const [showSaveAsOrUpdateMeta, setShowSaveAsOrUpdateMeta] = useState({
    show: false,
    isUpdating: false
  });
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(true);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const currentPageRef = useRef(1);
  // check if the default filter is applied on mount
  const defaultFilterAppliedRef = useRef(false);

  const statusOptions = [...new Set([...lienSearchStatuses, ...estoppelStatuses, ...surveyStatuses])].map(s =>
    typeof s === 'string' ? { label: s, value: s } : { label: s?.label ?? s?.value, value: s?.value ?? s?.label }
  );

  const [orderFilterMetadata, setOrderFilterMetadata] = useState(
    updatedServiceFiltereFields(
      SERVICES_RELATED_FILTERS(users, organizations, municipalityOptions, countyOptions, statusOptions, isClient(user?.role)),
      user
    )
  );

  const hasSidebarFiltersSelected = useMemo(() => {
    if (!orderFilterMetadata || !Array.isArray(orderFilterMetadata)) return false;
    return orderFilterMetadata.some(f => hasFilterValues(f, f.values));
  }, [orderFilterMetadata]);

  const pageSize = 25;
  const history = useHistory();
  const location = useLocation();
  const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, row: null });
  const [orderActionLoading, setOrderActionLoading] = useState({});

  const clearOrderActionLoading = useCallback(orderId => {
    setOrderActionLoading(prev => {
      if (!prev[orderId]) return prev;
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, open: false, row: null }));
  }, []);

  const getOrderLabel = row =>
    row?.service?.fileName || (row?.service ? getFullAddress(row.service) : '') || row?.accurateId || 'Order';

  const handleTogglePin = useCallback(async row => {
    const orderId = row._id;
    if (orderActionLoading[orderId]) return;

    setOrderActionLoading(prev => ({ ...prev, [orderId]: 'pin' }));

    try {
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.ORDER_PIN(orderId),
      method: row.pinned ? 'DELETE' : 'POST'
    });

    if (error) {
      toast.error(error);
      return;
    }

      const orderLabel = getOrderLabel(row);
      toast.success(row.pinned ? `Unpinned: ${orderLabel}` : `Pinned: ${orderLabel}`);

      if (typeof response?.pinnedOrderCount === 'number') {
        setPinnedOrderCount(response.pinnedOrderCount);
      } else {
        setPinnedOrderCount(prev => (row.pinned ? Math.max(0, prev - 1) : prev + 1));
      }

      const belongsOnTab = getTabBaseFilter(activeTab, isClient(user?.role)).includes(row.status);

      if (row.pinned && !belongsOnTab) {
        setTotalCount(count => {
          const nextTotal = Math.max(0, count - 1);
          setHasMoreData(currentPageRef.current < Math.ceil(nextTotal / pageSize));
          return nextTotal;
        });
        setOrders(prev => prev.filter(order => order._id !== orderId));
      } else {
        setOrders(prev =>
          sortOrdersPinnedFirst(
            prev.map(order => (order._id === orderId ? { ...order, pinned: !order.pinned } : order))
          )
        );
      }
      closeContextMenu();
    } finally {
      clearOrderActionLoading(orderId);
    }
  }, [orderActionLoading, closeContextMenu, clearOrderActionLoading, pinnedOrderCount, activeTab, user?.role, pageSize]);

  const patchOrderRow = useCallback(async (row, requestBody, successMessage, action) => {
    const orderId = row._id;
    if (orderActionLoading[orderId]) return;

    setOrderActionLoading(prev => ({ ...prev, [orderId]: action }));

    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORDER_WITH_ID(orderId),
        method: 'PUT',
        requestBody
      });

      if (error) {
        toast.error(error);
        return;
      }

      const { order: orderResponse, qualiaTaskError } = splitQualiaTaskError(response);
      setOrders(prev => prev.map(order => (order._id === orderId ? { ...order, ...orderResponse } : order)));
      toastOrderUpdateResult(qualiaTaskError, successMessage);
      closeContextMenu();
    } finally {
      clearOrderActionLoading(orderId);
    }
  }, [orderActionLoading, closeContextMenu, clearOrderActionLoading]);

  const handleMakeRush = useCallback(
    row => patchOrderRow(row, { rush: true }, 'Marked as rush', 'rush'),
    [patchOrderRow]
  );

  const handleRowContextMenu = useCallback((row, event) => {
    setContextMenu({
      open: true,
      x: event.clientX,
      y: event.clientY,
      row
    });
  }, []);

  const contextMenuItems = useMemo(() => {
    if (!contextMenu.row) return [];

    const items = getOrderContextMenuItems({
      row: contextMenu.row,
      isStaffUser,
      onPin: () => handleTogglePin(contextMenu.row),
      onRush: () => handleMakeRush(contextMenu.row)
    });

    return items.map(item => ({
      ...item,
      loading: orderActionLoading[contextMenu.row._id] === item.key
    }));
  }, [contextMenu.row, isStaffUser, orderActionLoading, handleTogglePin, handleMakeRush]);

  const columnsBase = useMemo(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
    return getOrderTableColumns({
      user: u,
      onTogglePin: handleTogglePin,
      orderActionLoading
    });
  }, [user._id, user.role, handleTogglePin, orderActionLoading]);

  const ordersColumnKeysSig = useMemo(
    () => columnsBase.map(c => `${c.key}:${c.hiddenByDefault ? 1 : 0}`).join('|'),
    [columnsBase]
  );

  const [ordersTablePrefs, setOrdersTablePrefs] = useState({
    columnOrder: [],
    hiddenColumnKeys: []
  });

  const [persistedOrdersTablePrefs, setPersistedOrdersTablePrefs] = useState({
    columnOrder: [],
    hiddenColumnKeys: []
  });

  const columnSummaries = useMemo(() => columnsBase.map(c => ({ key: c.key, label: c.label, hiddenByDefault: c?.hiddenByDefault })), [columnsBase]);

  const displayColumns = useMemo(() => {
    const hiddenSet = new Set(ordersTablePrefs.hiddenColumnKeys);
    const byKey = Object.fromEntries(columnsBase.map(c => [c.key, c]));
    const cols = ordersTablePrefs.columnOrder
      .filter(
        k =>
          byKey[k] &&
          (!hiddenSet.has(k) || ORDER_TABLE_COLUMNS_DISABLED.includes(k))
      )
      .map(k => byKey[k]);
    const pinIndex = cols.findIndex(col => col.key === 'pin');
    if (pinIndex > 0) {
      const [pinCol] = cols.splice(pinIndex, 1);
      return [pinCol, ...cols];
    }
    return cols;
  }, [columnsBase, ordersTablePrefs]);

  const preferencesDirty = useMemo(
    () => !isEqual(ordersTablePrefs, persistedOrdersTablePrefs),
    [ordersTablePrefs, persistedOrdersTablePrefs]
  );

  const hasPendingViewChanges = preferencesDirty || hasSidebarFiltersSelected || selectedFilterId;

  useEffect(() => {
    const defaultPrefs = normalizeOrdersTablePrefs(columnsBase, []);
    setOrdersTablePrefs(defaultPrefs);
    setPersistedOrdersTablePrefs(defaultPrefs);
  }, [ordersColumnKeysSig]);



  // Debounced search function
  const debouncedSearch = useCallback(
    query => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        setDebouncedSearchQuery(query);
      }, 500); // 500ms delay
    },
    []
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const fetchOrders = async (
    page = 1,
    sortKey = 'createdAt',
    sortDirection = 'desc',
    search = '',
    append = false,
    tab = TAB_KEYS.IN_PROGRESS,
    pageSizeOverride = null
  ) => {
    if (append && loadingMoreRef.current) return;

    // Fresh searches replace prior requests. Appends are guarded above so pages load in order.
    if (!append && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    if (append) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      loadingRef.current = true;
      currentPageRef.current = page;
      setLoading(true);
    }

    const filters = buildCombinedFilter(orderFilterMetadata, tab, isClient(user?.role));

    const effectivePageSize = pageSizeOverride || pageSize;

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_ORDERS,
      method: 'POST',
      requestBody: {
        page,
        pageSize: effectivePageSize,
        sort: { [sortKey]: sortDirection === 'asc' ? 1 : -1 },
        query: search.trim(),
        filters
      },
      signal
    });

    if (error == ABORT_ERROR) {
      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else {
        loadingRef.current = false;
        setLoading(false);
      }
      return;
    }

    if (error) {
      toast.error('Error while fetching orders.');
      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else {
        loadingRef.current = false;
        setLoading(false);
      }
      return;
    } else if (response && response.results) {
      if (append) {
        // Append new data to existing orders
        setOrders(prevOrders => [...prevOrders, ...response.results]);
      } else {
        // Replace orders with new data
        setOrders(response.results);
      }

      setTotalCount(response.resultCount);

      if (typeof response.pinnedOrderCount === 'number') {
        setPinnedOrderCount(response.pinnedOrderCount);
      }

      // Check if there's more data to load
      const totalPages = Math.ceil(response.resultCount / effectivePageSize);
      setHasMoreData(page < totalPages);
      currentPageRef.current = page;

      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Reset to first page when sort, tab, or filters change (but not searchQuery since it's debounced)
    setCurrentPage(1);
    setHasMoreData(true);
    fetchOrders(1, sortConfig.key, sortConfig.direction, debouncedSearchQuery, false, activeTab);
  }, [sortConfig, activeTab, orderFilterMetadata, filters, debouncedSearchQuery]);

  const getTabFromOrderStatus = orderStatuses => {
    if (
      isClient(user?.role) &&
      orderStatuses?.length > 0 &&
      orderStatuses.every(status => NEEDS_ATTENTION_STATUSES.includes(status))
    ) {
      return TAB_KEYS.NEEDS_ATTENTION;
    }
    const inProgressStatuses = isClient(user?.role) ? CLIENT_IN_PROGRESS_STATUSES : INPROGRESS_STATUSES;
    if (orderStatuses?.some(status => inProgressStatuses.includes(status))) {
      return TAB_KEYS.IN_PROGRESS;
    }
    if (orderStatuses?.some(status => DELIVERED_NOT_PAID_STATUSES.includes(status))) {
      return TAB_KEYS.DELIVERED_NOT_PAID;
    }
    if (orderStatuses?.some(status => COMPLETED_STATUSES.includes(status))) {
      return TAB_KEYS.COMPLETED;
    }
    if (orderStatuses?.some(status => CANCELLED_STATUSES.includes(status))) {
      return TAB_KEYS.CANCELLED;
    }
    return TAB_KEYS.IN_PROGRESS;
  };

  const handleOrderStatusChange = orderStatus => {
    setActiveTab(getTabFromOrderStatus(Array.isArray(orderStatus) ? orderStatus : [orderStatus]));
    // Update orderFilterMetadata with status filter
    setOrderFilterMetadata(prev =>
      prev.map(f =>
        f.key === 'status' ? { ...f, values: Array.isArray(orderStatus) ? orderStatus : [orderStatus] } : f
      )
    );
  };

  useEffect(() => {
    // Preselect sidebar filter metadata based on URL params
    let updatedMetadata = [...orderFilterMetadata];

    if (sortKey && sortDirection) {
      setSortConfig({
        key: sortKey,
        direction: sortDirection === 'asc' || sortDirection === 'desc' ? sortDirection : 'asc'
      });
    }

    if (range === ORDER_STATUS_RANGE.UPCOMING_CLOSINGS) {
      const { startDate, endDate } = getStartDateAndEndDateByRange(range);

      setActiveTab(TAB_KEYS.ALL);

      updatedMetadata = updatedMetadata.map(filter => {
        if (filter.key === 'closingDate') {
          return { ...filter, values: { startDate, endDate } };
        }
        if (filter.key === 'status') {
          return { ...filter, values: UPCOMING_CLOSINGS_STATUSES };
        }
        return filter;
      });
    }

    if (organizationId) {
      let orgIds = [];
      Array.isArray(organizationId) ? (orgIds = organizationId) : (orgIds = [organizationId]);

      updatedMetadata = updatedMetadata.map(filter => {
        if (filter.key === 'organization') {
          return {
            ...filter,
            values: orgIds
          };
        }
        return filter;
      });
    }

    if (range && orderStatus) {
      const { startDate, endDate } = getStartDateAndEndDateByRange(range);

      handleOrderStatusChange(orderStatus);

      if (orderStatus === 'Pending To Pay') {
        const dateKey =
          range === ORDER_STATUS_RANGE.PAST_CLOSING_DATE || range === ORDER_STATUS_RANGE.AWAITING_CLOSING
            ? 'closingDate'
            : 'dateDelivered';
        updatedMetadata = updatedMetadata.map(f => {
          if (f.key === dateKey) {
            return { ...f, values: { startDate, endDate } };
          } else if (f.key === 'status') {
            return { ...f, values: Array.isArray(orderStatus) ? orderStatus : [orderStatus] };
          }
          return f;
        });
      } else if (
        orderStatus.includes('Cancelled') ||
        orderStatus.includes('Refunded') ||
        orderStatus.includes('Refund Requested')
      ) {
        updatedMetadata = updatedMetadata.map(f => {
          if (f.key === 'dateCancelled') {
            return { ...f, values: { startDate, endDate } };
          } else if (f.key === 'status') {
            return { ...f, values: Array.isArray(orderStatus) ? orderStatus : [orderStatus] };
          }
          return f;
        });
      } else if (
        (Array.isArray(orderStatus) ? orderStatus.includes('Paid') : orderStatus === 'Paid') &&
        startDate &&
        endDate
      ) {
        //  set the Closed Files date filter via metadata
        updatedMetadata = updatedMetadata.map(f => {
          if (f.key === 'closedFile') {
            return { ...f, values: { startDate, endDate } };
          } else if (f.key === 'status') {
            return { ...f, values: Array.isArray(orderStatus) ? orderStatus : [orderStatus] };
          }
          return f;
        });
      }
    }

    if (orderStatus && !range) {
      // Preselect order statuses when provided without range
      handleOrderStatusChange(orderStatus);

      updatedMetadata = updatedMetadata.map(f => {
        if (f.key === 'status') {
          const statuses = Array.isArray(orderStatus) ? orderStatus : [orderStatus];
          return { ...f, values: statuses };
        }
        return f;
      });
    }

    const rawPipelineService = Array.isArray(serviceFromQuery) ? serviceFromQuery[0] : serviceFromQuery;
    if (
      rawPipelineService &&
      rawPipelineService !== 'All Services' &&
      ['Lien Search', 'Estoppel', 'Survey'].includes(rawPipelineService)
    ) {
      updatedMetadata = updatedMetadata.map(f =>
        f.key === 'orderType' ? { ...f, values: [rawPipelineService] } : f
      );
    }

    const processorIdsFromQuery = Array.isArray(assignedProcessorId)
      ? assignedProcessorId.filter(Boolean)
      : assignedProcessorId
        ? [assignedProcessorId]
        : [];
    if (processorIdsFromQuery.length > 0) {
      updatedMetadata = updatedMetadata.map(f =>
        f.key === 'assignedProcessors' ? { ...f, values: processorIdsFromQuery } : f
      );
    }

    setOrderFilterMetadata(updatedMetadata);

    if (organizationId) {
      if (organizationId === INDIVIDUAL_ORDERS) {
        setFilters(prev => ({
          ...prev,
          service: { isIndividualService: true, organization: null }
        }));
      } else {
        setFilters(prev => ({ ...prev, service: { organization: [organizationId] } }));
      }
    } else if (range && orderStatus) {
      const { startDate, endDate } = getStartDateAndEndDateByRange(range);
      handleOrderStatusChange(orderStatus);

      if (orderStatus === 'Pending To Pay') {
        if (range === ORDER_STATUS_RANGE.PAST_CLOSING_DATE || range === ORDER_STATUS_RANGE.AWAITING_CLOSING) {
          setFilters(prevFilters => ({
            ...prevFilters,
            service: { ...prevFilters.service, closingDate: { $gte: startDate, $lte: endDate } }
          }));
        } else {
          setFilters(prevFilters => ({ ...prevFilters, dateDelivered: { $gte: startDate, $lte: endDate } }));
        }
      } else if (
        orderStatus.includes('Cancelled') ||
        orderStatus.includes('Refunded') ||
        orderStatus.includes('Refund Requested')
      ) {
        // For cancelled use strict bounds similar to services pipeline ($gt/$lt)
        setFilters(prevFilters => ({ ...prevFilters, dateCancelled: { $gt: startDate, $lt: endDate } }));
      }
    } else if (orderStatus) {
      handleOrderStatusChange(orderStatus);
    } else if (range === ORDER_STATUS_RANGE.UPCOMING_CLOSINGS) {
      const { startDate, endDate } = getStartDateAndEndDateByRange(range);
      setFilters(prevFilters => ({
        ...prevFilters,
        service: { ...prevFilters.service, closingDate: { $gte: startDate, $lte: endDate } }
      }));
    } else if (orderId) {
      setFilters(prevFilters => ({ ...prevFilters, _id: orderId }));
    } else if (serviceId) {
      setFilters(prevFilters => ({ ...prevFilters, service: { ...prevFilters.service, _id: serviceId } }));
    }
  }, [organizationId, orderStatus, range, serviceFromQuery, assignedProcessorId, sortKey, sortDirection]);

  // Fetch saved filters only once on mount
  useEffect(() => {
    makeRESTApiRequest({
      endpoint: ENDPOINTS.GET_SAVED_FILTERS,
      method: 'GET'
    })
      .then(({ response, error }) => {
        if (error) {
          console.error('Error fetching saved filters:', error);
          return;
        }
        if (response) {
          setSavedFilters(response);
        }
      })
      .finally(() => setSavedFiltersLoading(false));
  }, []);

  // Auto-select default filter on mount if no query params are present
  useEffect(() => {
    if (defaultFilterAppliedRef.current) return;
    const hasQueryParams = !!(
      organizationId ||
      orderStatus ||
      range ||
      orderId ||
      serviceId ||
      orderTypeFromQuery ||
      serviceFromQuery ||
      assignedProcessorId ||
      sortKey ||
      sortDirection
    );

    // Only auto-select default filter if no query params, saved filters are loaded, and on mount
    if (!hasQueryParams && savedFilters.length > 0) {
      const defaultFilter = savedFilters.find(filter => filter.isDefault === true);
      if (defaultFilter) {
        defaultFilterAppliedRef.current = true;
        handleFilterSelect(defaultFilter);
      }
    }
  }, [savedFilters, organizationId, orderStatus, range, orderId, serviceId, orderTypeFromQuery, serviceFromQuery, assignedProcessorId, sortKey, sortDirection]);

  // Load more data when scrolling to bottom
  const handleLoadMore = () => {
    if (!loadingRef.current && !loadingMoreRef.current && hasMoreData) {
      const nextPage = currentPageRef.current + 1;
      setCurrentPage(nextPage);
      fetchOrders(nextPage, sortConfig.key, sortConfig.direction, searchQuery, true, activeTab);
    }
  };

  const handleSortChange = newSortConfig => {
    setSortConfig(newSortConfig);
  };

  const handleSearchChange = query => {
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      const query = searchQuery;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      setDebouncedSearchQuery(query);
      setCurrentPage(1);
      setHasMoreData(true);
      fetchOrders(1, sortConfig.key, sortConfig.direction, query, false, activeTab);
    }
  };

  const handleTabChange = tabKey => {
    setActiveTab(tabKey);
  };

  const handleFilterSidebarToggle = () => {
    setShowFilterSidebar(!showFilterSidebar);
  };

  const handleClearFilters = () => {
    const initialFilters = updatedServiceFiltereFields(
      SERVICES_RELATED_FILTERS(users, organizations, municipalityOptions, countyOptions, statusOptions, isClient(user?.role)),
      user
    );
    setOrderFilterMetadata(initialFilters);
    setSelectedFilterId(null);
    setSortConfig({ key: 'createdAt', direction: 'desc' });
  };

  const handleSaveFilter = async ({ viewName, setAsDefault }) => {
    const filtersPayload = orderFilterMetadata
      .filter(filter => hasFilterValues(filter, filter.values))
      .map(filter => ({
        key: filter.key,
        values: filter.values,
        operator: filter.operator || null
      }));

    const columnOrdering = buildColumnOrderingFromTablePrefs(columnsBase, ordersTablePrefs);

    setShowSaveMeta(prev => ({ ...prev, isUpdating: true }));
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.SAVED_FILTERS,
      method: 'POST',
      requestBody: {
        name: viewName,
        isDefault: setAsDefault,
        filters: filtersPayload,
        sort: sortConfig,
        ...(!isClient(user?.role) && { columnOrdering })
      }
    });

    if (error) {
      toast.error(error || 'Error while saving filter view.');
      setShowSaveMeta(prev => ({ ...prev, isUpdating: false }));
      return;
    }

    if (response) {
      toast.success('Filter view saved successfully.');

      setSavedFilters(prevFilters => {
        const updatedFilters = response.isDefault
          ? prevFilters.map(filter => ({ ...filter, isDefault: false }))
          : prevFilters;
        return [...updatedFilters, response].sort((a, b) => b.isDefault - a.isDefault);
      });
      setSelectedFilterId(response._id);
      !isClient(user?.role) && setPersistedOrdersTablePrefs(cloneOrdersTablePrefs(ordersTablePrefs));
    }
    setShowSaveMeta({ show: false, isUpdating: false });
  };

  const handleUpdatedFilter = async () => {
    const filtersPayload = orderFilterMetadata
      .filter(filter => hasFilterValues(filter, filter.values))
      .map(filter => ({
        key: filter.key,
        values: filter.values,
        operator: filter.operator || null
      }));

    const columnOrdering = buildColumnOrderingFromTablePrefs(columnsBase, ordersTablePrefs);

    setShowSaveAsOrUpdateMeta({ show: true, isUpdating: true });
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.UPDATE_SAVED_FILTER(selectedFilterId),
      method: 'PUT',
      requestBody: { filters: filtersPayload, sort: sortConfig, ...(!isClient(user?.role) && { columnOrdering }) }
    });

    if (error) {
      toast.error(error || 'Error while updating filter view.');
      return;
    }

    if (response) {
      toast.success('Filter updated successfully.');
      setSavedFilters(prevFilters => [
        ...prevFilters.map(filter => (filter._id === selectedFilterId ? response : filter))
      ]);
      !isClient(user?.role) && setPersistedOrdersTablePrefs(cloneOrdersTablePrefs(ordersTablePrefs));
    }
    setShowSaveAsOrUpdateMeta({ show: false, isUpdating: false });
  };

  const handleFilterSelect = async filter => {
    if (filter.filters && Array.isArray(filter.filters)) {
      // Reconstruct the filter metadata from saved filters
      const updatedMetadata = orderFilterMetadata.map(metaFilter => {
        const savedFilter = filter.filters.find(sf => sf.key === metaFilter.key);
        if (savedFilter) {
          return {
            ...metaFilter,
            operator: savedFilter.operator,
            values: savedFilter.values
          };
        }
        return {
          ...metaFilter,
          values: metaFilter.type === 'date' ? { startDate: null, endDate: null } : []
        };
      });

      setOrderFilterMetadata(updatedMetadata);
      setSelectedFilterId(filter._id);
      if (filter.sort?.key && filter.sort?.direction) {
        setSortConfig({
          key: filter.sort.key,
          direction: filter.sort.direction
        });
      }
    }
    if (!isClient(user?.role)) {
      if (filter.columnOrdering?.length > 0) {
        const normalizedTablePrefs = cloneOrdersTablePrefs(
          normalizeOrdersTablePrefs(columnsBase, filter.columnOrdering)
        );
        setOrdersTablePrefs(normalizedTablePrefs);
        setPersistedOrdersTablePrefs(normalizedTablePrefs);
      } else {
        const defaultPrefs = normalizeOrdersTablePrefs(columnsBase, []);
        setOrdersTablePrefs(defaultPrefs);
        setPersistedOrdersTablePrefs(defaultPrefs);
      }
    }
  };

  const handleRowClick = row => {
    setServiceMetadata({
      service: row.service,
      orderType: row.orderType,
      rowOrderId: row._id
    });

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('orderId');
    searchParams.set('serviceId', row.service._id);
    searchParams.set('orderType', row.orderType);
    searchParams.delete('reportType');
    const newSearch = searchParams.toString();
    history.replace({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : ''
    });
  };

  const handleFilterApply = filters => {
    setOrderFilterMetadata(filters);
    setShowFilterSidebar(false);
  };

  const handleLayoutToggle = () => {
    if (showOrderPreference) {
      setShowOrderPreference(false);
    } else {
      setShowFilterSidebar(false);
      setShowOrderPreference(true);
    }
  };

  const handleCancelPendingViewChanges = () => {
    handleClearFilters();
    if (!isClient(user?.role)) {
      const defaultPrefs = normalizeOrdersTablePrefs(columnsBase, []);
      setOrdersTablePrefs(defaultPrefs);
      setPersistedOrdersTablePrefs(defaultPrefs);
    }
  };

  const handleSavePendingViewChanges = () => {
    if (selectedFilterId) {
      setShowSaveAsOrUpdateMeta({ show: true, isUpdating: false });
    } else {
      setShowSaveMeta({ show: true, isUpdating: false });
    }
  };

  const refetchAfterUpdate = () => {
    const targetCount = orders && orders.length ? orders.length : pageSize;
    setCurrentPage(1);
    setHasMoreData(true);
    fetchOrders(1, sortConfig.key, sortConfig.direction, searchQuery, false, activeTab, targetCount + 1);
  };

  useEffect(() => {
    if (createdService) {
      // New orders only visible in ALL or IN_PROGRESS tabs
      const isRelevantTab =
        activeTab === TAB_KEYS.ALL ||
        activeTab === TAB_KEYS.IN_PROGRESS ||
        activeTab === TAB_KEYS.NEEDS_ATTENTION;

      if (!isRelevantTab) {
        onServiceHandled && onServiceHandled();
        return;
      }

      // refetch orders
      const targetCount = orders.length || pageSize;
      setCurrentPage(1);
      setHasMoreData(true);
      fetchOrders(1, sortConfig.key, sortConfig.direction, searchQuery, false, activeTab, targetCount);
      onServiceHandled && onServiceHandled();
    }
  }, [createdService]);

  const getTabBgColor = tabKey => {
    return ORDER_TABS_CONFIG[tabKey]?.bgColor || '#000000';
  };

  return (
    <div className="container-fluid px-0 py-1">
      <div className="px-2 px-xl-4">
        <div className="my-4">
          <div className="mb-0" style={{ borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem' }}>
            <div className="d-flex flex-wrap align-items-center mb-4" style={{ gap: '16px' }}>
              <div className="flex-fill overflow-auto d-flex align-items-center flex-wrap orders-filter-bar__tabs" style={{ minWidth: 0, gap: '12px' }}>
                {ORDER_TABS.filter(
                  tabKey => !ORDER_TABS_CONFIG[tabKey]?.clientOnly || isClient(user?.role)
                ).map(tabKey => {
                  const config = ORDER_TABS_CONFIG[tabKey];
                  const isActive = activeTab === tabKey;
                  const { Icon, label, textColor } = config;
                  return (
                    <Button
                      key={tabKey}
                      variant={'outline-light'}
                      type="button"
                      className="d-flex align-items-center gap-2 flex-shrink-0"
                      onClick={() => handleTabChange(tabKey)}
                      style={{
                        padding: '8px 18px',
                        border: isActive ? '1px solid transparent' : '1px solid #e5e7ea',
                        boxShadow: isActive ? 'inset 0 1px 2px rgba(0,0,0,0.04)' : 'none',
                        borderRadius: '8px',
                        backgroundColor: isActive ? getTabBgColor(tabKey) : 'transparent'
                      }}
                    >
                      <Icon className="icon-md" color={isActive ? textColor : 'black'} />
                      <span
                        className={`ml-2 ${isActive ? 'fw-bold' : 'fw-medium'} ${
                          isActive ? `text-${textColor === 'black' ? 'dark' : 'white'}` : 'text-dark'
                        }`}
                      >
                        {label}
                      </span>
                    </Button>
                  );
                })}
              </div>

              {(savedFiltersLoading || savedFilters.length > 0) && (
                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px', minWidth: 0 }}>
                  {savedFiltersLoading ? (
                    <ChipSkeletonLoader ariaLabel="Loading saved views" />
                  ) : (
                    <SavedFiltersList
                      savedFilters={savedFilters}
                      selectedFilterId={selectedFilterId}
                      onFilterSelect={handleFilterSelect}
                      setSavedFilters={setSavedFilters}
                      handleClearViewFilter={handleCancelPendingViewChanges}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="d-flex align-items-center mb-4">
              <div className="position-relative me-2" style={{ flex: 1, marginRight: 4 }}>
                <Search
                  className="icon-md position-absolute"
                  style={{
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: '#6c757d'
                  }}
                />
                <Form.Control
                  type="text"
                  size="lg"
                  className="bg-white search-input text-dark xsmallFont "
                  placeholder="Search orders by address, status, service, order ID..."
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{ paddingLeft: 40, border: '1px solid #e5e7ea', fontWeight: 'normal' }}
                />
              </div>

              <div className="d-flex align-items-center" style={{ marginLeft: 4, gap: 8 }}>
                <div className="position-relative">
                  <Button
                    variant="outline-light"
                    type="button"
                    className="d-flex align-items-center"
                    onClick={handleFilterSidebarToggle}
                    style={{
                      padding: '8px 12px',
                      gap: 6,
                      border: '1px solid #e5e7ea',
                      borderRadius: '8px',
                      backgroundColor: 'transparent',

                      flexShrink: 0,
                      color: 'black'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#f7f7f7';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Funnel size={16} className="align-middle" color="black" style={{ flexShrink: 0 }} />

                    <span className="fw-medium text-dark" style={{ fontSize: '14px', color: 'black' }}>
                      Filters
                    </span>
                    {hasSidebarFiltersSelected && (
                      <span
                        style={{
                          position: 'relative',
                          minWidth: 20,
                          height: 20,
                          backgroundColor: '#FFC72C',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#000',
                          padding: '2px',
                          lineHeight: 1
                        }}
                      >
                        {orderFilterMetadata.filter(f => hasFilterValues(f, f.values)).length}
                      </span>
                    )}
                  </Button>
                  {showFilterSidebar && (
                    <OrderFilterBuilder
                      key={selectedFilterId}
                      allAvailableFilters={orderFilterMetadata}
                      setOrderFilterMetadata={setOrderFilterMetadata}
                      onClose={() => setShowFilterSidebar(false)}
                      title="Filter Orders"
                      selectedFilterId={selectedFilterId}
                      onApplyFilters={filters => handleFilterApply(filters)}
                    />
                  )}
                </div>
                {!isClient(user?.role) && (
                  <Button
                    variant="outline-light"
                    type="button"
                    className="d-flex align-items-center justify-content-center position-relative"
                    onClick={handleLayoutToggle}
                    title={preferencesDirty ? 'Customize View — unsaved changes' : 'Customize View'}
                    aria-expanded={showOrderPreference}
                    aria-label={preferencesDirty ? 'Customize View, unsaved column changes' : 'Customize View'}
                    style={{
                      padding: '8px 12px',
                      border: showOrderPreference || preferencesDirty ? '1px solid #FACC15' : '1px solid #e5e7ea',
                      borderRadius: '8px',
                      backgroundColor:
                        showOrderPreference || preferencesDirty ? 'rgba(250, 204, 21, 0.12)' : 'transparent',
                      flexShrink: 0,
                      color: 'black'
                    }}
                  >
                    <LayoutList size={18} className="align-middle" color="black" style={{ flexShrink: 0 }} />
                    {preferencesDirty && (
                      <span
                        title="Unsaved layout changes"
                        style={{
                          position: 'absolute',
                          top: 5,
                          right: 5,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#FACC15',
                          border: '2px solid #fff',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
                          pointerEvents: 'none'
                        }}
                      />
                    )}
                  </Button>
                )}
              </div>
              {hasPendingViewChanges && (
                <>
                  <Button
                    type="button"
                    style={{
                      marginLeft: 4,
                      padding: '6px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: 'transparent',
                      border: '1px solid #e5e7ea'
                    }}
                    onClick={handleCancelPendingViewChanges}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#f7f7f7';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title={
                      preferencesDirty && hasSidebarFiltersSelected
                        ? 'Revert layout and filters'
                        : preferencesDirty
                          ? 'Revert layout changes'
                          : 'Revert filter changes'
                    }
                  >
                    <X size={16} />
                    Clear
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    style={{
                      backgroundColor: '#FFC72C',
                      borderColor: 'none',
                      boxShadow: 'none',
                      marginLeft: 4,
                      padding: '6px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onClick={handleSavePendingViewChanges}
                    title={
                      preferencesDirty && hasSidebarFiltersSelected
                        ? 'Save layout and filter view'
                        : preferencesDirty
                          ? 'Save layout'
                          : 'Save filter view'
                    }
                  >
                    <Save size={16} />
                    Save View
                  </Button>
                </>
              )}
            </div>
          </div>

          <DataTable
            key={`${sortConfig.key}-${sortConfig.direction}`}
            data={orders}
            columns={displayColumns}
            rowKey="_id"
            // Pagination
            pagination={false}
            // Sorting
            sortable={true}
            serverSort={true}
            defaultSort={sortConfig}
            onSortChange={handleSortChange}
            // Styling
            striped={false}
            hover={true}
            bordered={false}
            getRowClassName={row => (isStaffUser && row.pinned ? 'orders-table-row--pinned' : '')}
            showPinnedSectionDivider={isStaffUser}
            isPinnedRow={row => Boolean(row.pinned)}
            pinnedSectionCollapsedLimit={3}
            // for sticky header
            responsive={false}
            maxHeight="65vh"
            // States
            loading={loading}
            emptyMessage="No orders found"
            // Infinite scroll
            onBottomReached={handleLoadMore}
            loadingMoreData={loadingMore}
            bottomOffset={100}
            onRowClick={handleRowClick}
            onRowContextMenu={isStaffUser ? handleRowContextMenu : undefined}
          />
          <OrderRowContextMenu
            open={contextMenu.open}
            position={contextMenu.open ? { x: contextMenu.x, y: contextMenu.y } : null}
            items={contextMenuItems}
            onClose={closeContextMenu}
          />
          {totalCount > 0 && !loading && (
            <div className="text-muted mt-2">
              Showing <span className="text-dark font-weight-bold">{totalCount}</span> orders
            </div>
          )}
        </div>

        {!isClient(user?.role) && (
          <OrderPreference
            open={showOrderPreference}
            onClose={() => setShowOrderPreference(false)}
            columnDefs={columnSummaries}
            tablePrefs={ordersTablePrefs}
            onTablePrefsChange={setOrdersTablePrefs}
          />
        )}

        <ServiceSlidingBar
          service={serviceMetadata?.service}
          orderType={
            orderTypeFromQuery ||
            serviceMetadata?.orderType ||
            (orderId &&
              (reportType === ORDER_TYPES.SURVEY
                ? ORDER_TYPES.SURVEY
                : reportType === ORDER_TYPES.ESTOPPEL
                  ? ORDER_TYPES.ESTOPPEL
                  : ORDER_TYPES.LIEN_SEARCH))
          }
          onClose={() => setServiceMetadata(null)}
          refreshOrders={refetchAfterUpdate}
          orderId={orderId}
          serviceId={serviceId}
          reportType={reportType}
          rowOrderId={serviceMetadata?.rowOrderId}
        />

        {/* Save View Modal */}
        <SaveEditFilterModel
          show={showSaveMeta.show}
          onHide={() => setShowSaveMeta({ show: false, isUpdating: false })}
          onSave={handleSaveFilter}
          isUpdating={showSaveMeta.isUpdating}
        />
        <SaveAsOrUpdateModel
          show={showSaveAsOrUpdateMeta.show}
          onHide={() => setShowSaveAsOrUpdateMeta({ show: false, isUpdating: false })}
          onSaveAs={handleSaveFilter}
          onUpdate={handleUpdatedFilter}
          isUpdating={showSaveAsOrUpdateMeta.isUpdating || showSaveMeta.isUpdating}
        />
      </div>
    </div>
  );
}

export default Orders;
